<h2 mat-dialog-title><mat-icon class="title-icon">auto_awesome</mat-icon>Change {{data.project.name}} with AI</h2>

<mat-dialog-content class="content">
    @switch (step) {
        @case ('done') {
            <div role="status" data-testid="model-change-summary">
                <h3>{{madeCount ? 'Changes made' : 'No changes made'}}</h3>
                @if (summary && madeCount) {
                    <p class="summary">{{summary}}</p>
                }
                <ul class="changes">
                    @for (c of changes; track $index) {
                        <li [class.not-made]="!c.applied">
                            <mat-icon class="change-icon">{{c.applied ? 'check_circle' : 'block'}}</mat-icon>
                            <span>{{c.description}}@if (!c.applied) { <span class="reason">Not made: {{c.reason}}.</span> }</span>
                        </li>
                    }
                </ul>
                @if (madeCount) {
                    <p class="done-note">The project's insights have been updated. Compare it with current state on the analysis screen.</p>
                }
            </div>
        }
        @case ('confirm') { <ng-container *ngTemplateOutlet="confirmation"></ng-container> }
        @case ('applying') { <ng-container *ngTemplateOutlet="confirmation"></ng-container> }
        @default {
            <p class="intro">Describe the change you want to make to this project, such as adding people to a role or replacing one system
                with another. AI will ask if it needs to know more, then show you the changes to confirm before making them. Current state isn't affected.</p>
            <mat-form-field appearance="outline" class="full">
                <mat-label>The change</mat-label>
                <textarea matInput rows="4" [value]="description" (input)="description = $any($event.target).value" [attr.maxlength]="maxDescription"
                    [readonly]="step !== 'describe'" data-testid="model-change-description"></textarea>
            </mat-form-field>
            @if (step === 'describe' && !description) {
                <div class="examples">
                    <span class="examples-label">For example</span>
                    @for (e of examples; track e) {
                        <button mat-stroked-button type="button" (click)="useExample(e)">{{e}}</button>
                    }
                </div>
            }

            @for (a of answers; track $index) {
                <div class="answered">
                    <div class="question">{{a.question}}</div>
                    <div class="answer">{{a.answer}}</div>
                </div>
            }

            @if (step === 'questions' || (step === 'thinking' && questions.length)) {
                <div class="questions" data-testid="model-change-questions">
                    <div class="questions-intro">AI needs to know a little more first:</div>
                    @for (q of questions; track $index) {
                        <div class="question-text" [id]="'question-' + $index">{{q.question}}</div>
                        <mat-form-field appearance="outline" class="full">
                            <mat-label>Your answer</mat-label>
                            <input matInput [value]="q.answer" (input)="q.answer = $any($event.target).value" [attr.maxlength]="maxAnswer"
                                [readonly]="step !== 'questions'" (keydown.enter)="continue()" [attr.aria-describedby]="'question-' + $index">
                        </mat-form-field>
                    }
                </div>
            }

            @if (busy) {
                <div class="progress" role="status">
                    <mat-spinner diameter="20"></mat-spinner>
                    <span>Working out the changes…</span>
                </div>
            }
            @if (error) {
                <div class="error" role="alert">{{error}}</div>
            }
        }
    }
</mat-dialog-content>

<!-- the changes AI proposes, for the user to confirm -->
<ng-template #confirmation>
    <div data-testid="model-change-confirm">
        <h3>Check the changes</h3>
        <div class="described">{{description}}</div>
        @for (a of answers; track $index) {
            <div class="answered">
                <div class="question">{{a.question}}</div>
                <div class="answer">{{a.answer}}</div>
            </div>
        }
        @if (summary) {
            <p class="summary">{{summary}}</p>
        }
        <div class="list-label">These changes will be made to {{data.project.name}}:</div>
        <ul class="changes">
            @for (c of proposed; track $index) {
                <li><mat-icon class="change-icon proposed">arrow_right</mat-icon><span>{{c.description}}</span></li>
            }
        </ul>
        @if (notPossible.length) {
            <div class="list-label">These can't be made:</div>
            <ul class="changes">
                @for (c of notPossible; track $index) {
                    <li class="not-made"><mat-icon class="change-icon">block</mat-icon><span>{{c.description}} <span class="reason">{{c.reason}}.</span></span></li>
                }
            </ul>
        }
        @if (step === 'applying') {
            <div class="progress" role="status">
                <mat-spinner diameter="20"></mat-spinner>
                <span>Changing the models…</span>
            </div>
        }
        @if (error) {
            <div class="error" role="alert">{{error}}</div>
        }
    </div>
</ng-template>

<mat-dialog-actions align="end">
    @if (step === 'confirm' || step === 'applying') {
        <button mat-button type="button" (click)="editDescription()" [disabled]="step === 'applying'">Change the description</button>
        <button mat-button type="button" (click)="close()" [disabled]="step === 'applying'">Cancel</button>
        <button mat-flat-button color="accent" type="button" (click)="makeChanges()" [disabled]="step === 'applying'" data-testid="model-change-confirm-button">Make these changes</button>
    }@else if (step === 'done') {
        <button mat-button type="button" (click)="another()">Make another change</button>
        <button mat-flat-button color="accent" type="button" (click)="close()" data-testid="model-change-close">Close</button>
    }@else {
        <button mat-button type="button" (click)="close()">Cancel</button>
        <button mat-flat-button color="accent" type="button" (click)="continue()" [disabled]="!canContinue || busy" data-testid="model-change-continue">
            Continue
        </button>
    }
</mat-dialog-actions>
